import { Navigate, useLocation } from 'react-router'
import { useIsRestoring } from '@tanstack/react-query'
import { useSession } from '@/admin/hooks/use-session'
import { useOrg } from '@/admin/hooks/use-org'
import {
  useFeatureFlag,
  useFeatureFlags,
  type FeatureFlagKey,
} from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { Loader2 } from 'lucide-react'

function getLoginPath(returnTo?: string) {
  const slug = sessionStorage.getItem('tenantSlug')
  const base = slug ? `/${slug}/login` : '/login'
  if (returnTo && returnTo !== '/' && returnTo !== '/login') {
    return `${base}?next=${encodeURIComponent(returnTo)}`
  }
  return base
}

export function ProtectedRoute({
  flag,
  anyFlag,
  permission,
  anyPermission,
  internalOnly,
  adminOnly,
  children,
}: {
  /** Gate by a single feature flag (AND semantics if combined with anyFlag). */
  flag?: FeatureFlagKey
  /** Gate by ANY of these feature flags - page renders if at least one is on. */
  anyFlag?: FeatureFlagKey[]
  /** Gate by a single RBAC permission codename (AND with flag checks). */
  permission?: string
  /** Gate by ANY of these permission codenames - page renders if the user holds at least one. */
  anyPermission?: string[]
  /**
   * Internal staff only: bounce anyone who reaches the platform through a
   * Partnership. Prefer this over a codename ladder for whole-section gates
   * (/manage/*): most management codenames sit in the `Full Access` group
   * alone, so an `anyPermission` list locks out internal users on a narrower
   * role (Product Manager, Claims Reviewer, Viewer, ...) as well as partners.
   */
  internalOnly?: boolean
  /**
   * Admin/superadmin only: renders only for org admins (Membership.is_admin)
   * and staff/superusers - i.e. `usePermissions().isAdmin`. Stricter than a
   * `permission` codename gate: a non-admin role that merely holds the codename
   * (or `settings.view`) is refused. Use for admin-reserved surfaces where "can
   * open Settings" is deliberately too broad (e.g. the Welcome page editor).
   */
  adminOnly?: boolean
  children: React.ReactNode
}) {
  const { data, isLoading } = useSession()
  const {
    isLoading: isOrgLoading,
    partnerships,
    isPartnerAccess,
  } = useOrg()
  const enabled = useFeatureFlag(flag as FeatureFlagKey)
  const allFlags = useFeatureFlags()
  const {
    can,
    isAdmin,
    isLoading: arePermissionsLoading,
  } = usePermissions()
  const location = useLocation()
  // While PersistQueryClientProvider hydrates the IndexedDB cache, every query
  // fetch is paused: the session query sits at status 'pending' with
  // fetchStatus 'idle', so `isLoading` (isPending && isFetching) is FALSE with
  // `data` still undefined. Without this gate, the guard reads that window as
  // "not authenticated" and bounces a fully-authenticated full page load
  // (e.g. the post-impersonation `window.location.href = '/dashboard'`) to the
  // login page before /auth/session/ was ever fetched.
  const isRestoring = useIsRestoring()

  // Org context is awaited on every protected route, not just flag/permission
  // ones: the partner-context guard below reads it, and rendering the page
  // first would fire its requests without partner context and 403 them all.
  if (isRestoring || isLoading || isOrgLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  if (!data?.is_authenticated) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={getLoginPath(returnTo)} replace />
  }
  // Partner context lives in sessionStorage and is gone on every fresh tab.
  // Only the dashboard offers the launchpad that restores it, so a partner
  // landing anywhere else had no context in the headers. A SOLE partnership is
  // now resolved server-side on the first request (see
  // ``Audience._sole_partnership_pair``), so ``is_partner_access`` is already
  // true here and this guard is skipped - the deep-linked page renders straight
  // away with no round trip. This block remains for the MULTI-partnership case
  // (send them to the dashboard launchpad to pick), plus a fallback for a
  // backend that has not yet auto-resolved. ``partnerships`` holds only the
  // user's own PartnershipUserAccess rows, so it is empty for internal users
  // and this can never fire for them.
  const needsPartnerContext =
    partnerships.length > 0 &&
    !isPartnerAccess &&
    sessionStorage.getItem('activePartnerUuid') === null
  if (needsPartnerContext) {
    // Exactly one partnership but the server did not resolve it (older backend):
    // useOrg's effect is about to select it, but effects run after commit -
    // redirecting here would discard the deep link the dealer was emailed. Hold
    // on a spinner for that one render instead. The sessionStorage check above
    // ends it, so this cannot spin forever even if the refetch never reports
    // partner access.
    if (partnerships.length === 1)
      return (
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    if (location.pathname !== '/dashboard')
      return <Navigate to="/dashboard" replace />
  }
  // `partnerships` holds the user's own PartnershipUserAccess rows, so it is
  // non-empty only for external partners and empty for every internal user -
  // independent of which context they are currently acting in.
  if (internalOnly && partnerships.length > 0)
    return <Navigate to="/dashboard" replace />
  // A permission gate must not be read while /auth/rbac/my-permissions/ is in
  // flight: `can()`/`isAdmin` answer false in that window, so a cold direct
  // navigation would bounce a fully-authorised user to the dashboard.
  if ((permission || anyPermission || adminOnly) && arePermissionsLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  if (flag && !enabled) return <Navigate to="/dashboard" replace />
  if (anyFlag && !anyFlag.some((k) => allFlags[k] === true))
    return <Navigate to="/dashboard" replace />
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />
  if (permission && !can(permission)) return <Navigate to="/dashboard" replace />
  if (anyPermission && !anyPermission.some((p) => can(p)))
    return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

export function StaffProtectedRoute({
  children,
}: {
  children: React.ReactNode
}) {
  const { data, isLoading } = useSession()
  const location = useLocation()
  // Same restore-gate as ProtectedRoute: while the persisted cache hydrates,
  // the session query is paused (not loading, no data) and must not be read
  // as "not authenticated".
  const isRestoring = useIsRestoring()

  if (isRestoring || isLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  if (!data?.is_authenticated) {
    const returnTo = `${location.pathname}${location.search}`
    const suffix = returnTo && returnTo !== '/' && !returnTo.includes('/login') ? `?next=${encodeURIComponent(returnTo)}` : ''
    return <Navigate to={`/staff/login${suffix}`} replace />
  }
  if (!data?.user?.is_staff) return <Navigate to="/login" replace />
  return <>{children}</>
}
